/**
 * 风险规则管理工具
 * 自定义规则存在 localStorage，与内置规则合并使用
 */

export type RiskLevel = 'high' | 'medium' | 'low';

export interface RiskRule {
  id: string;
  level: RiskLevel;
  keyword: string;        // 匹配关键词
  description: string;     // 风险说明
  legalBasis: string;      // 法律依据（选填）
  isBuiltIn: boolean;      // 是否内置规则（不可删）
  createdAt: string;
}

const STORAGE_KEY = 'contract_risk_rules';

/** 读取自定义规则 */
export function loadCustomRiskRules(): RiskRule[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** 保存自定义规则 */
export function saveCustomRiskRules(rules: RiskRule[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rules));
}

/** 添加自定义规则 */
export function addCustomRiskRule(
  rule: Omit<RiskRule, 'id' | 'isBuiltIn' | 'createdAt'>
): RiskRule {
  const rules = loadCustomRiskRules();
  const newRule: RiskRule = {
    ...rule,
    id: 'rule_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 7),
    isBuiltIn: false,
    createdAt: new Date().toISOString(),
  };
  rules.push(newRule);
  saveCustomRiskRules(rules);
  return newRule;
}

/** 删除自定义规则（只允许删非内置的） */
export function deleteCustomRiskRule(id: string): boolean {
  const rules = loadCustomRiskRules();
  const filtered = rules.filter(r => r.id !== id);
  if (filtered.length === rules.length) return false;
  saveCustomRiskRules(filtered);
  return true;
}

/** 更新自定义规则 */
export function updateCustomRiskRule(id: string, updates: Partial<Omit<RiskRule, 'id' | 'isBuiltIn'>>): boolean {
  const rules = loadCustomRiskRules();
  const idx = rules.findIndex(r => r.id === id);
  if (idx === -1) return false;
  rules[idx] = { ...rules[idx], ...updates };
  saveCustomRiskRules(rules);
  return true;
}

/** 获取所有规则（内置 + 自定义），供 riskScanner 使用 */
export function getAllRiskRules(): RiskRule[] {
  // 内置规则
  const builtIn: RiskRule[] = [
    // 高风险
    { id: 'builtin-1', level: 'high', keyword: '口头约定', description: '未以书面形式确定合同内容，难以举证', legalBasis: '《民法典》第469条', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-2', level: 'high', keyword: '随时解除', description: '对方可随时解除合同，我方权益无保障', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-3', level: 'high', keyword: '无违约责任', description: '未约定违约责任条款，违约成本为零', legalBasis: '《民法典》第577条', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-4', level: 'high', keyword: '自动续约', description: '未明确续约条件，可能被动续约', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-5', level: 'high', keyword: '放弃追偿', description: '条款明示或暗示放弃追索权，严重损害我方利益', legalBasis: '《民法典》第535条', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-6', level: 'high', keyword: '无限责任', description: '责任上限未设限，可能产生巨额赔偿', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-7', level: 'high', keyword: '单方变更', description: '对方可单方面变更合同内容，我方无议价能力', legalBasis: '', isBuiltIn: true, createdAt: '' },
    // 中风险
    { id: 'builtin-8', level: 'medium', keyword: '书面通知', description: '通知义务未明确期限，可能产生争议', legalBasis: '《民法典》第140条', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-9', level: 'medium', keyword: '争议解决', description: '争议解决方式未明确约定', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-10', level: 'medium', keyword: '保密义务', description: '保密范围和期限未明确', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-11', level: 'medium', keyword: '不可抗力', description: '不可抗力条款定义过宽，对方可能滥用', legalBasis: '《民法典》第180条', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-12', level: 'medium', keyword: '验收标准', description: '验收标准模糊，易产生质量争议', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-13', level: 'medium', keyword: '付款条件', description: '付款前提条件不明确，可能造成拖欠', legalBasis: '', isBuiltIn: true, createdAt: '' },
    // 低风险
    { id: 'builtin-14', level: 'low', keyword: '通知地址', description: '送达地址未确认，通知效力存在争议', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-15', level: 'low', keyword: '合同份数', description: '合同正本份数及持有方式未明确', legalBasis: '', isBuiltIn: true, createdAt: '' },
    { id: 'builtin-16', level: 'low', keyword: '未尽事宜', description: '未尽事宜处理机制未约定', legalBasis: '', isBuiltIn: true, createdAt: '' },
  ];
  const custom = loadCustomRiskRules();
  return [...builtIn, ...custom];
}
